import { useState, useEffect } from "react";
import {
  createUser,
  assignRoleToUser,
  getRoles,
} from "../../../../core/services/keycloakApi";
import { createPlamboUserProfile } from "../../../../core/services/profile.service";

class UserCreationError extends Error {
  constructor(message) {
    super(message);
    this.name = "UserCreationError";
  }
}

const assertCreated = (response, stepLabel) => {
  if (response?.status !== 201) {
    throw new UserCreationError(
      `${stepLabel} failed with status ${response?.status ?? "unknown"}. Expected 201.`,
    );
  }
};

const useSignupForm = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    jobTitles: [],
    globalRoles: [],
    skills: [],
    expertise: [],
    userRoles: {
      name: "",
      id: "",
    },
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!success) return undefined;

    const timer = setTimeout(() => setSuccess(""), 4000);

    return () => clearTimeout(timer);
  }, [success]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e, overrides = {}) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const {
      firstName,
      lastName,
      email,
      username,
      jobTitles,
      globalRoles,
      skills: formSkills,
      expertise: formExpertise,
      userRoles,
    } = formData;
    const skills = overrides.skills ?? formSkills;
    const expertise = overrides.expertise ?? formExpertise;

    if (
      !firstName ||
      !lastName ||
      !email ||
      !username ||
      !jobTitles?.length ||
      !globalRoles?.length ||
      !userRoles?.id
    ) {
      setError(
        "Please fill in all required fields including at least one Job Title, RACI Role and User Role.",
      );
      return;
    }

    try {
      setLoading(true);
      const tempPassword = `${username}@123`;
      const keycloakPayload = {
        username,
        email,
        firstName,
        lastName,
        enabled: true,
        emailVerified: true,
        credentials: [
          { type: "password", value: tempPassword, temporary: false },
        ],
      };

      const keycloakRes = await createUser(keycloakPayload);
      assertCreated(keycloakRes, "Keycloak user creation");

      const userId = keycloakRes.data?.data?.id;
      if (!userId) {
        throw new UserCreationError(
          "Keycloak user creation returned 201 but no user id was provided.",
        );
      }


      await assignRoleToUser(userId, {
        id: userRoles.id,
        name: userRoles.name,
        description: userRoles.description,
      });

      const profileRes = await createPlamboUserProfile({
        userId,
        globalRoles,
        jobTitles: jobTitles,
        userName: username,
        email: email,
        firstName: firstName,
        lastName: lastName,
        skills,
        expertise,
      });
      assertCreated(profileRes, "Plambo profile creation");

      setSuccess(`User created successfully!\n Password: ${tempPassword}`);

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        username: "",
        jobTitles: [],
        globalRoles: [],
        skills: [],
        expertise: [],
          userRoles: {
        name: "",
        id: "",
      },
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "User creation failed. No partial account was saved.",
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    error,
    success,
    loading,
    handleChange,
    handleSubmit,
    clearSuccess: () => setSuccess(""),
  };
};

export default useSignupForm;
